import React, { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { employeeUser } from 'modules/core/store/collections/users'
import app, { FlashType } from 'modules/core/store/models/app'

import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Props {
  employee: Employee
}

const SendInvitationBanner: React.FC<Props> = ({ employee }) => {
  const [invitationSend, setInvitationSend] = useState(false)
  const user = employeeUser(employee)
  const alreadyInvited = user.invited
  const alreadyJoined = user.confirmed

  useEffect(() => {
    setInvitationSend(false)
  }, [employee.id])

  useAsyncData(async () => {
    return policyAuthorizations.authorizePolicies('employee.invite', [employee])
  }, [])

  const canInvite = policyAuthorizations.for('employee.invite', employee)

  const isVisible =
    employee.policy.can('shareProfileEmail') &&
    !!user.attributes.email &&
    canInvite &&
    !employee.isTerminated &&
    ((alreadyInvited && !alreadyJoined) || (!alreadyInvited && !alreadyJoined))

  const onInviteUser = async () => {
    const api = ApiClient()
    try {
      await api.post('/invitations', {
        access_id: employee.attributes.access_id,
      })
      setInvitationSend(true)
      showFlashMessage('correct', i18n.t('employees.invitation_sent'))
    } catch (error) {
      if (typeof error !== 'object' || error === null) {
        throw error
      }

      const errors = JSON.parse(JSON.stringify(error))

      if (!('payload' in errors) || !('messages' in errors.payload)) {
        throw error
      }

      const errorMessage = errors.payload.messages
      showFlashMessage('wrong', errorMessage)
    }
  }

  const showFlashMessage = (type: FlashType, message: string) => {
    app.setFlash({
      type,
      message,
    })
  }

  if (!isVisible) return null

  const bannerText = () => {
    if (alreadyJoined) return

    if (alreadyInvited)
      return i18n.t('employee_form.uninvited_again', {
        user: user.preferredName,
      })

    return i18n.t('employee_form.uninvited', {
      user: user.preferredName,
    })
  }

  const buttonText = () => {
    if (alreadyInvited && !invitationSend) return i18n.t('employee_form.resend_invitation')

    if (invitationSend) return i18n.t('employees.invitation_sent')

    return i18n.t('employee_form.send_invitation')
  }

  const date = dateToString(moment(user.invited_on), 'ui')

  const invitation_date = alreadyInvited
    ? i18n.t('employee_form.invitation_date', {
        date: date,
      })
    : ''

  return (
    <Box
      flexDirection="row"
      background="champagne400"
      borderRadius={{
        all: 'abs008',
      }}
      paddingY="s16"
      paddingX="s24"
      alignItems="center"
      width="full"
      justifyContent="spaceBetween"
    >
      <Box flexDirection="row" gap="s8">
        <Text.Normal color="grey800">{bannerText()}</Text.Normal>
        <Text.Normal color="grey800">{invitation_date}</Text.Normal>
      </Box>
      <Box>
        <Button type="secondary" onClick={onInviteUser}>
          {buttonText()}
        </Button>
      </Box>
    </Box>
  )
}

export default observer(SendInvitationBanner)
